import { APITypes } from '../primary-record-types';

export type Handle1UpHealthRedirectReturnValue = {
    shouldBlockAppLoad: boolean;
};

const clientTypeKey = '1upHealthRedirectSourceClientType';
const safelySetClientTypeInLocalStorage = (clientType: string) => {
    try {
        localStorage.setItem(clientTypeKey, clientType);
    } catch (e) {
        /*
          do nothing.  Local storage may not be available.  If this is the case, we'll end up
          redirecting to the prod primary record web app.  This will prevent a completely broken
          experience for real-world users
        */
        console.error('Unable to set client type in local storage', e);
    }
};

/**
 * Safely retrieves the client type from local storage.  If local storage is not available, we'll
 * default to prod-web
 */
const safelyGetClientTypeFromLocalStorage = (): string => {
    try {
        return localStorage.getItem(clientTypeKey) || 'prod-web';
    } catch (e) {
        return 'prod-web';
    }
};

const sourceClientTypeUrls: Record<APITypes.sharedTypes.ClientType, string> = {
    'mobile-dev': 'primary-record-app://redirect',
    mobile: 'primary-record-app://redirect',
    'local-web': 'http://localhost:19006/redirect',
    'dev-web': 'https://dev.primaryrecord.app/redirect',
    'prod-web': 'https://www.primaryrecord.app/redirect',
};

/**
 * 1Up health only allows configuration of a single redirect URI per client (environment).  Because of this,
 * we cannot redirect back to the client directly from one up health.  One up health also does not provide any
 * user context on the redirect, so we cannot redirect to some backend service.
 *
 * To get around this, the general flow is:
 * 1. The client navigates to the prod or dev primary record web app providing the 1up health connect url and source client type.
 * 2. The web app stores the original client type and navigates to the specified 1up health connect url.
 * 3. The user completes the 1up health flow and is redirected back to the web app.
 * 4. The web app recognizes it's handling a redirect BACK from 1up health
 * 5. The web app navigates back to the source app dictated by the original client type.
 */
export function handle1UpHealthRedirect() {
    const location = window?.location?.href;

    // this may not even be the web app.  This will not exist in mobile
    if (!location) {
        return { shouldBlockAppLoad: false };
    }

    // we're on the web, but it may not be involved in the 1up health redirect at all
    if (!location.includes('handle-1up-redirect')) {
        return { shouldBlockAppLoad: false };
    }

    const url = new URL(location);
    const sourceClientType = url.searchParams.get('sourceClientType');
    const connectUrl = url.searchParams.get('connectUrl');

    // if client type and connectUrl are specified, we're handling a redirect TO 1up health
    if (sourceClientType && connectUrl) {
        safelySetClientTypeInLocalStorage(sourceClientType);
        const decodedConnectUrl = decodeURIComponent(connectUrl);

        // sanitize the connect url to ensure it starts with the 1up health base url
        const baseUrl = 'https://api.1up.health';
        if (!decodedConnectUrl.startsWith(baseUrl)) {
            console.error('1up health connect url must start with ' + baseUrl);
            return { shouldBlockAppLoad: false };
        }

        const redirectUrl = baseUrl + decodedConnectUrl.split(baseUrl).join('');
        window.location.href = redirectUrl;
    } else {
        // if client type and connectUrl are not specified, we're handling a redirect BACK from 1up health
        const clientType = safelyGetClientTypeFromLocalStorage();
        // @ts-expect-error TS7053
        const destinationUrl = sourceClientTypeUrls[clientType] || sourceClientTypeUrls['prod-web']; // default to prod-web to not break existing apps during migration
        window.location.href = destinationUrl;
    }

    // block app load until we've navigated to the destination url.  Rendering the app will slow down the redirect and create a choppy UX
    return { shouldBlockAppLoad: true };
}
